import React from 'react';
import { Country }  from 'country-state-city';
import { formatPhone } from './phone';
import { Text } from 'grommet';
import { formatName } from './names';
import { byCountry } from 'country-code-lookup';

export const formatAddress = (
    address,
    showAdditionalDetails=false, // additional details = company name, phone, and email
    inline=false // if true, returns a plain string instead of JSX
) => {
    if (!address) return '';
    const {
        address1,
        address2,
        street, // fallback for address1
        city,
        state,
        postal_code,
        breakAfterAddress2,
        country,
        email,
        phone,
        first_name,
        company_name
    } = address;
    const streetAddress = address1 || street; // use address1, fall back to street

    // Build country name
    let countryName = country;
    if (country && country.length === 2) {
        countryName = Country.getCountryByCode(country)?.name;
    }

    // Inline mode - return plain string
    if (inline) {
        const parts = [];
        if (streetAddress) parts.push(streetAddress);
        if (address2) parts.push(address2);
        const cityStateZip = [
            city,
            state ? (city ? `, ${state}` : state) : '',
            postal_code || ''
        ].filter(Boolean).join(' ').replace(' ,', ',');
        if (cityStateZip.trim()) parts.push(cityStateZip.trim());
        if (countryName && !isUSA(country)) parts.push(countryName);
        return parts.join(', ');
    }

    if (isEmailOnlyAddress(address)) {
        return showAdditionalDetails ? (
            <>
                {company_name ? (<><Text weight="bold">{company_name}</Text><br/></>) : ""}
                {first_name ? (<>{formatName(address, true)}{email && <br/>}</>) : ""}
                {email}
                {phone && <br/>}
                {formatPhone(phone)}
            </>
        ) : (
            <></> // no details to show
        );
    }

    const line1 = `${streetAddress || ''}${address2 ? ` ${address2}` : ''}`;
    const line2 = `${city || ''}${state && city ? ', ' : ''}${state || ''} ${`${postal_code || ''}`}`;
    return (
        <>
            {showAdditionalDetails && company_name ? (<><Text weight="bold">{company_name}</Text><br/></>) : ""}
            {showAdditionalDetails && first_name ? (<>{formatName(address, true)}<br/></>) : ""}
            {line1}
            {breakAfterAddress2 && line2 && line1 ? (<br/>) : (", ")}
            {line2}
            {countryName ? (<>{line2 && <br/>}{countryName}</>) : ''}
            {showAdditionalDetails ? (<>{countryName && <br/>}{email}{phone && <br/>}{formatPhone(phone)}</>) : ''}
        </>);
}

export const isUSA = (country) => {
    if (!country) return false;
    return ['united states', 'us', 'usa', 'u.s.a.', 'united states of america', 'u.s.'].includes(country.toLowerCase());
}

export const formatCityStateCountry = (address, skipCountryIfUSA=true) => {
    if (!address) return '';
    const {
        city,
        state,
        postal_code,
        country
    } = address;
    let countryName = country;
    let hideCountry = false;
    if (country && country.length == 2) {
        countryName = Country.getCountryByCode(country)?.name;
    }
    if (skipCountryIfUSA && isUSA(country)) {
        hideCountry = true;
    }
    return `${city || ''}${state && city ? ', ' : ''}${state || ''} ${`${postal_code || ''}`}${hideCountry || (!countryName) ? '' : `, ${countryName}`}`;
}

export const isEmailOnlyAddress = (address) => {
    const {
        address1,
        address2,
        city,
        state,
        postal_code,
        email
    } = address || {}
    return ((!address1 && !address2 && !city && !state && !postal_code) && email);
}

export const getCountryFlagEmoji = (country) => {
    if (!country) return '';
    const code = country.length > 2 ? byCountry(country)?.iso2 : country;
    if (!code) return '';
    const codePoints = code
        .toUpperCase()
        .split('')
        .map(char =>  127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
}